import React, { useEffect, useState } from "react";
import { Eye, EyeOff, Key } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import CustomSelect from "./ui/CustomSelect";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

const AddUser = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [roleId, setRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      setError("");
      const response = await apiCall("get", "/get-all-roles", null, navigate);
      console.log("Raw API response:", response);

      // Simplified response handling
      let rolesData = [];
      
      if (response?.data?.success) {
        // Check multiple possible locations for roles data
        if (Array.isArray(response.data.data)) {
          rolesData = response.data.data;
        } else if (Array.isArray(response.data.roles)) {
          rolesData = response.data.roles;
        } else if (Array.isArray(response.data)) {
          rolesData = response.data;
        }
        
        console.log("Processed roles data:", rolesData);

        if (rolesData.length === 0) {
          setError("No roles available");
          return;
        }

        setRoles(rolesData);

        // Set default role
        const queryParams = new URLSearchParams(location.search);
        const preSelectedRoleId = queryParams.get("roleId");
        
        if (preSelectedRoleId && rolesData.some(role => role.role_id.toString() === preSelectedRoleId)) {
          setRoleId(preSelectedRoleId);
        } else if (rolesData.length > 0) {
          setRoleId(rolesData[0].role_id.toString());
        }
      } else {
        setError("Failed to load roles");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setError(error.response?.data?.message || "Error fetching roles");
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Debug effect to log current state
  useEffect(() => {
    console.log("Current roles:", roles);
    console.log("Current roleId:", roleId);
    console.log("Roles loading:", rolesLoading);
  }, [roles, roleId, rolesLoading]);

  const selectedRoleValue = roleId ? {
    value: roleId,
    label: roles.find(role => role.role_id.toString() === roleId)?.role_name || "Select Role"
  } : null;

  const roleOptions = roles.map(role => ({
    value: role.role_id.toString(),
    label: role.role_name
  }));

  console.log("Role options for CustomSelect:", roleOptions);
  console.log("Selected role value:", selectedRoleValue);

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let generatedPassword = "";
    for (let i = 0; i < 12; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      generatedPassword += charset[randomIndex];
    }
    setPassword(generatedPassword);
  };

  const handleAddUser = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    if (!username.trim() || !email.trim() || !password.trim() || !roleId) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    try {
      const payload = { username, email, password, role_id: roleId };
      const response = await apiCall("post", "/create-user", payload, navigate);

      if (response?.data?.success) {
        setSuccess("User added successfully!");
        setTimeout(() => navigate("/all-users"), 2000);
      } else {
        setError(response?.data?.message || "Failed to add user.");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Error adding user.");
    } finally {
      setLoading(false);
    }
  };

  if (rolesLoading) return <LoadingComponent />;

  return (
    <div style={{ overflow: "visible" }}>
      <div className="card h-100 p-0 radius-12" style={{ overflow: "visible" }}>
        <div className="card-body p-40" style={{ overflow: "visible" }}>
          <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <label htmlFor="username-value" className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Username <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    id="username-value"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter Username"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <label htmlFor="email" className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Email <span className="text-danger-600">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control radius-8"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email address"
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <label htmlFor="new-password" className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Password <span className="text-danger-600">*</span>
                  </label>
                  <div style={{ position: "relative", width: "100%" }}>
                    <input
                      type={passwordVisible ? "text" : "password"}
                      className="form-control radius-8"
                      id="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      style={{ paddingRight: "50px" }}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        padding: "0.25rem",
                        cursor: "pointer",
                        color: "#6c757d",
                      }}
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      type="button"
                      style={{
                        position: "absolute",
                        right: "40px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        padding: "0.25rem",
                        cursor: "pointer",
                        color: "#6c757d",
                      }}
                      onClick={generatePassword}
                      aria-label="Generate password"
                    >
                      <Key size={18} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20" style={{ position: "relative", zIndex: 1000 }}>
                  <label htmlFor="roleId" className="form-label fw-semibold text-primary-light text-sm mb-8">
                    Role <span className="text-danger-600">*</span>
                  </label>
                  {roles.length > 0 ? (
                    <CustomSelect
                      name="roleId"
                      value={selectedRoleValue}
                      onChange={(selected) => {
                        console.log("CustomSelect changed to:", selected);
                        setRoleId(selected?.value || "");
                      }}
                      options={roleOptions}
                      placeholder="Select Role"
                      isClearable={false}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        menu: base => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  ) : (
                    <div className="form-control radius-8">
                      {error || "No roles available"}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="col-sm-12">
                <div className="mb-20">
                  {error && (
                    <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                      {error}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  )}
                  {success && (
                    <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                      {success}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccess("")}
                      ></button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <Link
                    to="/all-users"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8 text-decoration-none"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary-600 border border-primary-600 text-md px-24 py-12 radius-8"
                    onClick={handleAddUser}
                    disabled={loading || roles.length === 0}
                  >
                    {loading ? "Please wait..." : "Add User"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;